'use server'

import { apiRequest, ApiResponse } from '../request'

export type RndsEventType =
    | 'VACCINATION'
    | 'ALLERGY'
    | 'MEDICATION'
    | 'LAB_RESULT'
    | 'ENCOUNTER'
    | 'DIAGNOSTIC'
    | 'PROCEDURE'

export type RndsStatus =
    | 'PENDING'
    | 'SUBMITTED'
    | 'ACCEPTED'
    | 'REJECTED'
    | 'ERROR'

export interface RndsConfiguration {
    id: number
    companyId: number
    name: string
    cnes: string
    cnpj: string
    uf: string
    active: boolean
    environment: 'PRODUCTION' | 'HOMOLOGATION'
    certificatePath?: string
    certificatePassword?: string
    clientId?: string
    clientSecret?: string
    accessToken?: string
    tokenExpiresAt?: string
    settings?: any
    createdAt: string
    updatedAt: string
    _count?: {
        transactions: number
    }
}

export interface RndsTransaction {
    id: number
    configurationId: number
    configuration?: {
        id: number
        name: string
    }
    eventType: RndsEventType
    clientId?: number
    client?: {
        id: number
        name: string
        cpf?: string
    }
    professionalId?: number
    professional?: {
        id: number
        name: string
        cns?: string
    }
    fhirResource?: any
    fhirResourceId?: string
    status: RndsStatus
    errorMessage?: string
    submittedAt?: string
    respondedAt?: string
    metadata?: any
    createdAt: string
    updatedAt: string
}

export interface CreateRndsConfigurationData {
    name: string
    cnes: string
    cnpj: string
    uf: string
    active?: boolean
    environment?: 'PRODUCTION' | 'HOMOLOGATION'
    clientId?: string
    clientSecret?: string
    settings?: any
}

export interface UpdateRndsConfigurationData
    extends Partial<CreateRndsConfigurationData> {}

export interface VaccinationEventData {
    clientId: number
    professionalId: number
    vaccine: {
        code: string
        name: string
        manufacturer?: string
        batch?: string
    }
    administrationDate: string
    doseNumber?: number
}

export interface LabResultEventData {
    clientId: number
    professionalId: number
    examId: number
    examResultId: number
}

export interface RndsStats {
    totalConfigurations: number
    totalTransactions: number
    byStatus: Record<RndsStatus, number>
    byEventType: Record<RndsEventType, number>
}

// Configuration CRUD
export async function getRndsConfigurations(): Promise<
    ApiResponse<RndsConfiguration[]>
> {
    return apiRequest<RndsConfiguration[]>('rnds/configurations', {})
}

export async function getRndsConfigurationsByCompany(
    companyId: number
): Promise<ApiResponse<RndsConfiguration[]>> {
    return apiRequest<RndsConfiguration[]>(
        `rnds/configurations/company/${companyId}`,
        {}
    )
}

export async function getRndsConfiguration(
    id: number
): Promise<ApiResponse<RndsConfiguration>> {
    return apiRequest<RndsConfiguration>(`rnds/configurations/${id}`, {})
}

export async function createRndsConfiguration(
    data: CreateRndsConfigurationData
): Promise<ApiResponse<RndsConfiguration>> {
    return apiRequest<RndsConfiguration>(
        'rnds/configurations',
        { data },
        'post'
    )
}

export async function updateRndsConfiguration(
    id: number,
    data: UpdateRndsConfigurationData
): Promise<ApiResponse<RndsConfiguration>> {
    return apiRequest<RndsConfiguration>(
        `rnds/configurations/${id}`,
        { data },
        'put'
    )
}

export async function deleteRndsConfiguration(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`rnds/configurations/${id}`, {}, 'delete')
}

export async function testRndsConfiguration(
    id: number
): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest<{ success: boolean; message: string }>(
        `rnds/configurations/${id}/test`,
        {},
        'post'
    )
}

export async function refreshRndsToken(
    id: number
): Promise<ApiResponse<RndsConfiguration>> {
    return apiRequest<RndsConfiguration>(
        `rnds/configurations/${id}/refresh-token`,
        {},
        'post'
    )
}

// Transactions
export async function getRndsTransactions(filters?: {
    configurationId?: number
    eventType?: RndsEventType
    status?: RndsStatus
    clientId?: number
    startDate?: string
    endDate?: string
}): Promise<ApiResponse<RndsTransaction[]>> {
    return apiRequest<RndsTransaction[]>('rnds/transactions', {
        params: filters,
    })
}

export async function getRndsTransaction(
    id: number
): Promise<ApiResponse<RndsTransaction>> {
    return apiRequest<RndsTransaction>(`rnds/transactions/${id}`, {})
}

// RNDS Operations
export async function submitVaccination(
    configurationId: number,
    data: VaccinationEventData
): Promise<ApiResponse<RndsTransaction>> {
    return apiRequest<RndsTransaction>(
        `rnds/configurations/${configurationId}/vaccination`,
        { data },
        'post'
    )
}

export async function submitLabResult(
    configurationId: number,
    data: LabResultEventData
): Promise<ApiResponse<RndsTransaction>> {
    return apiRequest<RndsTransaction>(
        `rnds/configurations/${configurationId}/lab-result`,
        { data },
        'post'
    )
}

export async function retryRndsTransaction(
    id: number
): Promise<ApiResponse<RndsTransaction>> {
    return apiRequest<RndsTransaction>(
        `rnds/transactions/${id}/retry`,
        {},
        'post'
    )
}

export async function getRndsStats(): Promise<ApiResponse<RndsStats>> {
    return apiRequest<RndsStats>('rnds/stats', {})
}

export const RNDS_STATUS_LABELS: Record<RndsStatus, string> = {
    PENDING: 'Pendente',
    SUBMITTED: 'Enviada',
    ACCEPTED: 'Aceita',
    REJECTED: 'Rejeitada',
    ERROR: 'Erro',
}

export const RNDS_STATUS_COLORS: Record<RndsStatus, string> = {
    PENDING: 'badge-warning',
    SUBMITTED: 'badge-info',
    ACCEPTED: 'badge-success',
    REJECTED: 'badge-error',
    ERROR: 'badge-error',
}

export const RNDS_EVENT_TYPE_LABELS: Record<RndsEventType, string> = {
    VACCINATION: 'Vacinacao',
    ALLERGY: 'Alergia',
    MEDICATION: 'Medicamento',
    LAB_RESULT: 'Resultado Laboratorial',
    ENCOUNTER: 'Atendimento',
    DIAGNOSTIC: 'Diagnostico',
    PROCEDURE: 'Procedimento',
}
